import type { ColumnDef } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil } from "@fortawesome/pro-solid-svg-icons";
import CurrencyFormat from "react-currency-format";
import { projectStatusText } from "@components/SowHeader/utils/sowHeaderHelpers";
import { causeLabel } from "../constants/causes";
import { isHttpUrl } from "../constants/url";
import type { ChangeOrder } from "../types";

// eslint-disable-next-line @typescript-eslint/no-var-requires
const strftime = require("strftime");

export type FieldLabels = Record<string, string | undefined>;

interface BuildColumnsArgs {
  /** The account's field-labels, for status label overrides. */
  fieldLabels?: FieldLabels;
  /** The project's currency unit, e.g. "$". */
  currency: string;
  /** The account's strftime short-date pattern, e.g. "%m/%d/%Y". */
  shortDateFormat?: string;
  /** Opens a change request's edit page. */
  onEdit: (id: string) => void;
}

const DASH = "—";

/** At most 2 decimals, trailing zeros trimmed: 12 -> "12", 12.333 -> "12.33". */
export const formatHours = (hours: number) =>
  hours.toLocaleString("en-US", { maximumFractionDigits: 2 });

export const statusText = (row: ChangeOrder, fieldLabels?: FieldLabels) =>
  row.referenceOnly || row.status === "reference_only"
    ? "Reference only"
    : projectStatusText(row.status, fieldLabels);

export const buildColumns = ({
  fieldLabels,
  currency,
  shortDateFormat,
  onEdit,
}: BuildColumnsArgs): ColumnDef<ChangeOrder>[] => [
  {
    accessorKey: "label",
    header: "Number",
    // The number opens the change request's project (reference-only ones
    // have none); the pencil edits the change request itself.
    cell: ({ row }) => {
      const { id, label, referenceOnly, childProjectId } = row.original;
      return (
        <span className="change-requests-number">
          {referenceOnly || !childProjectId ? (
            <span className="change-requests-number__label">{label}</span>
          ) : (
            <a
              href={`/projects/${childProjectId}/project_editor`}
              className="change-requests-number__label"
            >
              {label}
            </a>
          )}
          <button
            type="button"
            className="change-requests-number__edit"
            aria-label={`Edit ${label}`}
            title="Edit cause, note and reference"
            onClick={() => onEdit(id)}
          >
            <FontAwesomeIcon icon={faPencil} />
          </button>
        </span>
      );
    },
  },
  {
    id: "cause",
    header: "Cause",
    cell: ({ row }) => (
      <div>
        {causeLabel(row.original.cause)}
        {row.original.causeNote && (
          <>
            <br />
            <small className="text-muted">{row.original.causeNote}</small>
          </>
        )}
      </div>
    ),
  },
  {
    id: "status",
    header: "Status",
    cell: ({ row }) => (
      <span className="change-requests-status">
        {statusText(row.original, fieldLabels)}
      </span>
    ),
  },
  {
    id: "hours",
    header: "Hours",
    cell: ({ row }) => (
      <div className="w-100 text-right">
        {row.original.referenceOnly || row.original.hours === null
          ? DASH
          : `+${formatHours(row.original.hours)}`}
      </div>
    ),
  },
  {
    id: "revenue",
    header: "Revenue",
    cell: ({ row }) => (
      <div className="w-100 text-right">
        {row.original.referenceOnly || row.original.revenue === null ? (
          DASH
        ) : (
          <CurrencyFormat
            displayType="text"
            prefix={`+${currency}`}
            isNumericString
            thousandSeparator
            value={row.original.revenue.toFixed(2)}
          />
        )}
      </div>
    ),
  },
  {
    id: "reference",
    header: "Reference",
    cell: ({ row }) => {
      const { externalReference, externalUrl } = row.original;
      if (!externalReference && !externalUrl) return DASH;
      // Only http(s) URLs become links; anything else is shown as text.
      if (!externalUrl || !isHttpUrl(externalUrl)) {
        return externalReference || externalUrl;
      }
      return (
        <a href={externalUrl} target="_blank" rel="noopener noreferrer">
          {externalReference || externalUrl}
        </a>
      );
    },
  },
  {
    id: "author",
    header: "Created",
    cell: ({ row }) => (
      <div>
        {row.original.userName || DASH}
        <br />
        <small className="text-muted">
          {strftime(
            shortDateFormat || "%m/%d/%Y",
            new Date(row.original.createdAt)
          )}
        </small>
      </div>
    ),
  },
];
